import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { propOr } from 'ramda';
import { Formik, Field } from 'formik';
import * as yup from 'yup';
import { TextField, Button, Box, CircularProgress, Typography } from '@mui/material';
import cn from 'classnames';

/* API */
import { apiRequest } from 'lib/API';
import { usePostUpdateWorkSearch } from 'lib/hooks/workSearchQueries';

import PhoneNumberInput from 'components/forms/PhoneNumberInput';
import { DatePicker } from '@mui/x-date-pickers';

/* Analytics */
import { trackEvent } from 'lib/analytics';

/* i18n */
import { useIntl } from 'react-intl';

import styles from 'styles/apply-job-form.module.scss';

import BrowseDocuments from 'components/BrowseDocuments';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

function Resea({ setOpen, selectedActivity, editting = false, week, onSubmit }) {
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [editMode, setEditMode] = useState(true);
  const [refreshConter, setRefreshConter] = useState(0);
  const [documentsLinks, setDocumentsLinks] = useState([]);
  const [dateError, setDateError] = useState('');
  const intl = useIntl();
  const postUpdateWorkSearchMutation = usePostUpdateWorkSearch(
    editting,
    () => {
      setOpen(false);
      setSubmitting(false);
    },
    () => {
      setSubmitting(false);
      setSaveError(true);
    }
  );

  useEffect(() => {
    setRefreshConter(refreshConter + 1);
    if (selectedActivity && selectedActivity.upload_documents && selectedActivity.upload_documents.length > 0) {
      setDocumentsLinks([...selectedActivity.upload_documents]);
    } else {
      setDocumentsLinks([]);
    }
  }, [selectedActivity, editting]);

  useEffect(() => {
    setEditMode(!editting || Boolean(selectedActivity?.draft));
  }, [editting, selectedActivity?.draft, selectedActivity?.worksearch_id]);

  useEffect(() => {
    let cancelled = false;
    const documentsWithNoLinks = documentsLinks.filter((d) => !d.downloadUrl);
    if (documentsWithNoLinks.length > 0) {
      documentsWithNoLinks.forEach((doc) => {
        apiRequest(
          'get',
          `/users/me/worksearch/${encodeURIComponent(selectedActivity.worksearch_id)}?file_uuid=${encodeURIComponent(
            doc.file_uuid
          )}`
        )
          .then((res) => {
            if (!cancelled) {
              setDocumentsLinks((prev) =>
                prev.map((d) => (d.file_uuid === doc.file_uuid ? { ...d, downloadUrl: res.activity?.download_url } : d))
              );
            }
          })
          .catch(() => {});
      });
    }
    return () => {
      cancelled = true;
    };
  }, [JSON.stringify(documentsLinks)]);

  const phoneRegExp = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/; // eslint-disable-line no-useless-escape
  const emailRegExp = /^\S+@\S+\.\w\w+$/;

  const validationSchema = yup.object({
    contact_name: yup
      .string(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' }))
      .trim()
      .required(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' })),
    contact_phone_number: yup
      .string(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' }))
      .matches(phoneRegExp, intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' }))
      .required(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' })),
    contact_email: yup.string(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' })).matches(emailRegExp, {
      message: intl.formatMessage({ id: 'forms.onboarding.emailValidation' }),
      excludeEmptyString: true,
    }),
    date_of_workshop: yup
      .string(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' }))
      .required(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' })),
  });

  const defaultValues = {
    contact_name: '',
    contact_phone_number: '',
    contact_email: '',
    date_of_workshop: null,
    notes: '',
    upload_documents: [],
    draft: false,
  };

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={validationSchema}
      initialValues={
        selectedActivity
          ? {
              ...selectedActivity,
              contact_name: propOr('', 'contact_name', selectedActivity),
              contact_phone_number: propOr('', 'contact_phone_number', selectedActivity),
              contact_email: propOr('', 'contact_email', selectedActivity),
              notes: propOr('', 'notes', selectedActivity),
              upload_documents: [],
              date_of_workshop: new Date(
                selectedActivity.date_of_workshop.slice(0, 4),
                selectedActivity.date_of_workshop.slice(5, 7) - 1,
                selectedActivity.date_of_workshop.slice(8, 10)
              ),
            }
          : defaultValues
      }
      onSubmit={async (values) => {
        setSubmitting(true);
        setSaveError(false);
        let year = values.date_of_workshop.getFullYear();
        let month = values.date_of_workshop.getMonth() + 1;
        let day = values.date_of_workshop.getDate();
        if (month < 10) month = '0' + month;
        if (day < 10) day = '0' + day;
        const vals4submit = {
          ...values,
          activity_type: 'participate_in_resea',
          activity_date: `${year}-${month}-${day}`,
          date_of_workshop: `${year}-${month}-${day}`,
        };
        vals4submit.upload_documents = [
          ...documentsLinks.map((doc) => ({ file_uuid: doc.file_uuid, filename: doc.filename })),
          ...values.upload_documents.map((doc) => ({ filename: doc.name, upload_contents: doc.contents })),
        ];
        if (onSubmit && typeof onSubmit === 'function') {
          return onSubmit(vals4submit);
        }
        if (editting) {
          trackEvent('WEEK_VIEWACTIVITY_SAVE');
        } else {
          trackEvent('WEEK_SAVEACTIVITY_RESEA');
        }
        postUpdateWorkSearchMutation.mutate(vals4submit);
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, touched, values, setFieldValue }) => {
        function isValidDate(d) {
          return d instanceof Date && !isNaN(d);
        }
        const handleDateChange = (date) => {
          const minDate = new Date(week.from.slice(0, 4), week.from.slice(5, 7) - 1, week.from.slice(8, 10));
          const maxDate = new Date(week.to.slice(0, 4), week.to.slice(5, 7) - 1, week.to.slice(8, 10));
          if (isValidDate(date)) {
            setFieldValue('date_of_workshop', date);
            if (date < minDate || date > maxDate) {
              setDateError('The selected date is outside the valid range');
            } else {
              setDateError('');
            }
          } else {
            let error = intl.formatMessage({ id: 'jobSearchActivity.validation.invalidDateFormat' });
            if (Object.is(date, null)) {
              error = intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' });
            }
            setDateError(error);
            setFieldValue('date_of_workshop', '');
          }
        };
        useEffect(() => {
          if (touched.date_of_workshop) {
            handleDateChange(values.date_of_workshop);
          }
        }, [touched.date_of_workshop]);
        return (
          <form onSubmit={handleSubmit} className={styles.loginFlow__form}>
            {submitting ? (
              <Box m={2} display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : (
              <>
                <div className={styles.fields}>
                  <div>
                    <TextField
                      disabled={submitting || (editting && !editMode)}
                      InputLabelProps={{ required: true }}
                      error={touched.contact_name && Boolean(errors.contact_name)}
                      fullWidth
                      helperText={touched.contact_name && errors.contact_name}
                      label={intl.formatMessage({ id: 'jobSearchActivity.form.contactName' })}
                      name="contact_name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      classes={{ root: styles.textFieldRoot }}
                      type="text"
                      value={values.contact_name}
                      variant="filled"
                      inputProps={{
                        title: intl.formatMessage({ id: 'jobSearchActivity.form.contactName' }),
                      }}
                      id="name-resea-work-search-field"
                    />
                  </div>
                  <div>
                    <TextField
                      disabled={submitting || (editting && !editMode)}
                      InputLabelProps={{ required: true }}
                      error={touched.contact_phone_number && Boolean(errors.contact_phone_number)}
                      fullWidth
                      helperText={touched.contact_phone_number && errors.contact_phone_number}
                      label={intl.formatMessage({ id: 'jobSearchActivity.form.contactPhoneNumber' })}
                      name="contact_phone_number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      classes={{ root: styles.textFieldRoot }}
                      type="text"
                      value={values.contact_phone_number}
                      variant="filled"
                      inputProps={{
                        title: intl.formatMessage({ id: 'jobSearchActivity.form.contactPhoneNumber' }),
                      }}
                      InputProps={{
                        inputComponent: PhoneNumberInput,
                      }}
                      id="phone-resea-work-search-field"
                    />
                  </div>
                  <div>
                    <TextField
                      disabled={submitting || (editting && !editMode)}
                      error={touched.contact_email && Boolean(errors.contact_email)}
                      fullWidth
                      helperText={touched.contact_email && errors.contact_email}
                      label={intl.formatMessage({ id: 'jobSearchActivity.form.contactEmail' })}
                      name="contact_email"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      classes={{ root: styles.textFieldRoot }}
                      type="text"
                      value={values.contact_email}
                      variant="filled"
                      inputProps={{
                        title: intl.formatMessage({ id: 'jobSearchActivity.form.contactEmail' }),
                      }}
                      id="email-resea-work-search-field"
                    />
                  </div>
                  <Box sx={{ position: 'relative' }}>
                    <DatePicker
                      onError={(newError) => {
                        if (['minDate', 'maxDate'].indexOf(newError) > -1) {
                          setDateError(intl.formatMessage({ id: 'jobSearchActivity.form.dateOutOfRange' }));
                        } else {
                          setDateError(newError);
                        }
                      }}
                      disabled={submitting || (editting && !editMode)}
                      label={`${intl.formatMessage({ id: 'jobSearchActivity.resea.dateOfResea' })} *`}
                      minDate={new Date(week.from.slice(0, 4), week.from.slice(5, 7) - 1, week.from.slice(8, 10))}
                      maxDate={new Date(week.to.slice(0, 4), week.to.slice(5, 7) - 1, week.to.slice(8, 10))}
                      slotProps={{
                        textField: {
                          variant: 'filled',
                          helperText:
                            touched.date_of_workshop && errors.date_of_workshop ? errors.date_of_workshop : dateError,
                          error: dateError || (touched.date_of_workshop && Boolean(errors.date_of_workshop)),
                          id: 'date-resea-work-search-field',
                        },
                      }}
                      onChange={(newValue) => {
                        setFieldValue('date_of_workshop', newValue);
                      }}
                      value={values.date_of_workshop}
                    />
                    <HelpOutlineIcon
                      sx={{
                        color: (theme) => theme.palette.grey['800'],
                        fontSize: 16,
                        position: 'absolute',
                        top: { xs: 61, md: 53 },
                        left: 0,
                      }}
                    />
                    <span className={cn(styles.fieldDescription, styles.fieldDescriptionSemiClose)}>
                      {intl.formatMessage({ id: 'jobSearchActivity.form.dateRange' })}
                    </span>
                  </Box>
                </div>
                <div className={styles.boxes}>
                  <div>
                    <Box pt={2} pb={1}>
                      <span className={styles.fieldLabel}>
                        {intl.formatMessage({ id: 'jobSearchActivity.form.uploadDocuments' })}
                      </span>
                    </Box>
                    <Field
                      name="upload_documents"
                      disabled={submitting || (editting && !editMode)}
                      component={BrowseDocuments}
                      label="jobSearchActivity.resea.documentHint"
                      buttonId="browse-document-resea-work-search-button"
                    />
                    <div className={styles.downloadLinks}>
                      {documentsLinks.map((doc) =>
                        doc.downloadUrl ? (
                          <a href={doc.downloadUrl} key={doc.downloadUrl}>
                            {doc.filename}
                          </a>
                        ) : (
                          <span key={JSON.stringify(doc)}>...</span>
                        )
                      )}
                    </div>
                  </div>
                  <div>
                    <Box pt={2} pb={1}>
                      <span className={styles.fieldLabel}>
                        {intl.formatMessage({ id: 'jobSearchActivity.form.notes' })}
                      </span>
                    </Box>
                    <TextField
                      id="notes-resea-work-search-field"
                      placeholder={intl.formatMessage({ id: 'jobSearchActivity.form.notes' })}
                      multiline
                      rows={5}
                      variant="outlined"
                      InputProps={{ style: { fontSize: 12, height: '112px' } }}
                      value={values.notes}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      name="notes"
                      disabled={submitting || (editting && !editMode)}
                      inputProps={{
                        title: intl.formatMessage({ id: 'jobSearchActivity.form.notes' }),
                      }}
                    />
                  </div>
                </div>
              </>
            )}
            <Box display="flex" justifyContent="space-between" mt={4}>
              {saveError ? (
                <Typography color="error" variant="body2" sx={{ alignSelf: 'center' }}>
                  {intl.formatMessage({ id: 'app.error' })}
                </Typography>
              ) : (
                <div />
              )}
              <Box display="flex">
                <Box mr={1}>
                  <Button
                    type="button"
                    disabled={submitting}
                    size="large"
                    onClick={() => setOpen(false)}
                    variant="outlined"
                    color="secondary"
                    id="cancel-resea-work-search-button"
                  >
                    {submitting ? (
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">{intl.formatMessage({ id: 'jobSearchActivity.form.cancel' })}</span>
                      </div>
                    ) : (
                      intl.formatMessage({ id: 'jobSearchActivity.form.cancel' })
                    )}
                  </Button>
                </Box>
                {editting && !editMode ? (
                  <Button
                    color="secondary"
                    disabled={submitting || !selectedActivity?.draft}
                    size="large"
                    variant="contained"
                    onClick={(evt) => {
                      evt.preventDefault();
                      trackEvent('WEEK_VIEWACTIVITY_EDIT');
                      setEditMode(true);
                    }}
                    id="edit-resea-work-search-button"
                  >
                    {intl.formatMessage({ id: 'layout.header.edit' })}
                  </Button>
                ) : (
                  <Button
                    color="secondary"
                    disabled={submitting || Boolean(dateError) || (editting && !editMode)}
                    size="large"
                    type="submit"
                    variant="contained"
                    id="save-resea-work-search-button"
                  >
                    {submitting ? (
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">{intl.formatMessage({ id: 'jobSearchActivity.form.save' })}</span>
                      </div>
                    ) : (
                      intl.formatMessage({ id: 'jobSearchActivity.form.save' })
                    )}
                  </Button>
                )}
              </Box>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
}

Resea.propTypes = {
  setOpen: PropTypes.func,
  selectedActivity: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  editting: PropTypes.bool,
  week: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default Resea;
